/**
 * Аптеки сети. Открыта ли аптека прямо сейчас, считает сервер в ташкентском
 * времени — по часам браузера она открывалась бы в чужом поясе.
 */
import type { Translate } from '../../ui/translate'
import type { Store } from './api'

export type StoreFilter = 'all' | 'open' | 'day'

/** Порядок режимов в шторке фильтров; подписи — `stores:filters.*`. */
export const FILTERS: StoreFilter[] = ['open', 'day', 'all']

/**
 * Схема проезда в Яндекс.Картах — провайдере карт проекта. С координатами
 * строим маршрут от места, где стоит покупатель; без них ищем по адресу.
 */
export function routeUrl(store: Pick<Store, 'address' | 'lat' | 'lng'>): string {
  if (store.lat !== null && store.lng !== null) {
    return `https://yandex.uz/maps/?rtext=~${store.lat},${store.lng}&rtt=auto`
  }
  return `https://yandex.uz/maps/?text=${encodeURIComponent(`${store.address}, Ташкент`)}`
}

/** Режим работы одной строкой: круглосуточно или часы буднего дня. */
export function hoursText(store: Store, t: Translate): string {
  if (store.is247) return t('stores:allDay')
  const day = store.hours.find((item) => item !== null)
  return day ? `${day.open} – ${day.close}` : t('stores:hoursUnknown')
}

/** Фильтр в параметры запроса: отбор делает сервер, а не экран. */
export function toQuery(query: string, filter: StoreFilter) {
  return {
    q: query.trim(),
    openNow: filter === 'open' ? true : undefined,
    is247: filter === 'day' ? true : undefined,
  }
}

export interface Point {
  lat: number
  lng: number
}

/** Расстояние по прямой в метрах (гаверсинус): для «ближе — выше» этого хватает. */
export function distanceMeters(a: Point, b: Point): number {
  const rad = Math.PI / 180
  const dLat = (b.lat - a.lat) * rad
  const dLng = (b.lng - a.lng) * rad
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2
  return 2 * 6_371_000 * Math.asin(Math.sqrt(h))
}

/** Расстояние до каждой аптеки; без координат — null. */
export function withDistance(
  stores: Store[],
  me: Point,
): { store: Store; meters: number | null }[] {
  return stores.map((store) => ({
    store,
    meters:
      store.lat !== null && store.lng !== null
        ? distanceMeters(me, { lat: store.lat, lng: store.lng })
        : null,
  }))
}

/** Ближние — выше, аптеки без координат — в конце в прежнем порядке. */
export function nearestFirst(
  stores: Store[],
  me: Point,
): { store: Store; meters: number | null }[] {
  return withDistance(stores, me).sort((a, b) => (a.meters ?? Infinity) - (b.meters ?? Infinity))
}

/** «850 м», «1,2 км», «12 км» — до километра метрами, дальше с одной цифрой. */
export function distanceText(meters: number, t: Translate, language: string): string {
  if (meters < 1000)
    return t('stores:distance.m', { n: Math.max(10, Math.round(meters / 10) * 10) })
  const km = meters / 1000
  const n = new Intl.NumberFormat(language === 'uz' ? 'uz-UZ' : 'ru-RU', {
    maximumFractionDigits: km < 10 ? 1 : 0,
  }).format(km)
  return t('stores:distance.km', { n })
}
